import React from "react";
import PropTypes from "prop-types";
import { Button, ButtonGroup, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { icons } from "../../../config/config";
import { isValidDiscount, normalizeProductKey, toFloat } from "../utils/shopGridDiscounts";

const perfectScrollbarOptions = {
  wheelSpeed: 2,
  wheelPropagation: false,
};

const CartDropdownToggler = React.forwardRef(function CartDropdownToggler(props, ref) {
  const { color } = props;
  return (
    <Button
      style={{ backgroundColor: color, borderColor: color }}
      className="font-weight-bold btn-inline d-inline-flex align-items-center"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        props.onClick(e);
      }}
    >
      {props.children}
    </Button>
  );
});

CartDropdownToggler.displayName = "CartDropdownToggler";

export const ShopCartDropdown = React.memo(function ShopCartDropdown({
  cart,
  displayName,
  color,
  elementsMap,
  hasColli,
  maxDescriptionLength,
  richiediDatiVendita,
  discountValidationMessage,
  discountsLoadError,
  onSendRequest,
  sendDisabled,
  sendLabel,
  getScontoFinalValue,
  getEffectiveDiscountRaw,
  setCartValue,
}) {
  return (
    <div className="cartArea">
      {discountsLoadError ? (
        <div className="alert alert-danger mb-2" role="alert" style={{ whiteSpace: "pre-wrap" }}>
          Configurazione sconti non disponibile
        </div>
      ) : null}
      <Dropdown drop="up" as={ButtonGroup}>
      {discountValidationMessage ? (
        <div className="alert alert-warning mb-0" role="alert" style={{ whiteSpace: "pre-wrap" }}>
          {/* {discountValidationMessage} */}
          <div className="mt-2">Imposta gli sconti per procedere.</div>
        </div>
      ) : (
      
        <Button
          style={{ backgroundColor: color, borderColor: color }}
          className="font-weight-bold btn-inline d-inline-flex align-items-center"
          onClick={sendDisabled ? null : onSendRequest}
          disabled={sendDisabled}
        >
          {sendLabel}
          <span className="svg-icon svg-icon-md svg-icon-light-primary ml-2">
            <SVG src={toAbsoluteUrl(icons.go)} />
          </span>
        </Button>
      )}
        {Object.keys(cart).length > 0 ? (
          <Dropdown.Menu className="p-0 m-0 dropdown-menu-right dropdown-menu-anim dropdown-menu-top-unround dropdown-menu-lg">
            <PerfectScrollbar
              options={perfectScrollbarOptions}
              className="scroll"
              style={{ maxHeight: "calc(100vh - 100px)", position: "relative" }}
            >
              {Object.keys(cart)
                .map((id, key) => {
                  const elementKey = normalizeProductKey(id);
                  const element = elementsMap.get(elementKey) || elementsMap.get(id);
                  if (!element) return null;
                  const hasInvalidDiscountForCartItem =
                    richiediDatiVendita && cart[id] > 0 && !isValidDiscount(getEffectiveDiscountRaw(id));

                  return (
                    <React.Fragment key={`cart_${id}`}>
                      <div
                        className="d-flex align-items-center justify-content-between p-8"
                        style={hasInvalidDiscountForCartItem ? { borderLeft: "4px solid #dc3545" } : undefined}
                      >
                        <div className="d-flex flex-column mr-2">
                          <span className="font-weight-bold text-dark-75 font-size-lg text-hover-primary">
                            {element.name}
                            <Button
                              key={`btn_${key}`}
                              variant="light-success"
                              className="btn-xs btn-icon ml-2"
                              onClick={() => setCartValue(id, 0)}
                            >
                              <i className="ki ki-close icon-xs"></i>
                            </Button>
                          </span>
                          <span className="text-muted">
                            {element.description && element.description.length > 0 ? (
                              element.description.length > maxDescriptionLength ? (
                                <OverlayTrigger
                                  key={`ovl${key}`}
                                  placement="auto"
                                  delay={{ show: 250, hide: 400 }}
                                  trigger={["hover", "focus", "click"]}
                                  overlay={(props) => (
                                    <Tooltip id="button-tooltip" {...props}>
                                      {element.description}
                                    </Tooltip>
                                  )}
                                >
                                  <p>{element.description.substring(0, maxDescriptionLength) + "..."}</p>
                                </OverlayTrigger>
                              ) : (
                                element.description
                              )
                            ) : null}
                          </span>
                          {hasInvalidDiscountForCartItem ? (
                            <div className="text-danger mt-2">
                              Inserisci un valore di sconto oppure rimuovi il prodotto dal carrello.
                            </div>
                          ) : null}
                          <div className="d-flex align-items-center mt-2">
                            {element.quantity ? (
                              <>
                                <span className="mr-1 text-dark-75 font-size-3">
                                  {element.quantity}
                                  {typeof element.cost !== "undefined" ? ` (${toFloat(element.cost, 0)}€)` : null}
                                </span>
                                <span className="text-muted mr-1"> &#215; </span>
                                <span className="font-weight-bold mr-2 text-dark-75 font-size-3">{cart[id]} colli</span>
                                <span className="text-muted mr-1">=</span>
                                <span className="mr-2 text-dark-75 font-size-3">
                                  {parseInt(element.quantity, 10) * toFloat(cart[id], 0)} {displayName}
                                  {typeof element.cost !== "undefined" ? (
                                    <>
                                      (
                                      {getScontoFinalValue(id) ? (
                                        <>
                                          <span style={{ textDecoration: "line-through" }}>
                                            {(toFloat(cart[id], 0) * toFloat(element.cost, 0)).toFixed(2)}€
                                          </span>
                                          <span className="ml-2">
                                            {(
                                              (1 - getScontoFinalValue(id) / 100) *
                                              toFloat(cart[id], 0) *
                                              toFloat(element.cost, 0)
                                            ).toFixed(2)}
                                          </span>
                                        </>
                                      ) : (
                                        (toFloat(cart[id], 0) * toFloat(element.cost, 0)).toFixed(2)
                                      )}
                                      €)
                                    </>
                                  ) : null}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="mr-1 text-dark-75 font-size-3">
                                  {typeof element.cost !== "undefined" ? `${toFloat(element.cost, 0)}€` : null}
                                </span>
                                <span className="text-muted mr-1"> &#215; </span>
                                <span className="font-weight-bold mr-2 text-dark-75 font-size-3">{cart[id]}</span>
                                <span className="text-muted mr-1">=</span>
                                <span className="mr-2 text-dark-75 font-size-3">
                                  {typeof element.cost !== "undefined" ? (
                                    <>
                                      {getScontoFinalValue(id) ? (
                                        <>
                                          <span style={{ textDecoration: "line-through" }}>
                                            {(toFloat(cart[id], 0) * toFloat(element.cost, 0)).toFixed(2)}€
                                          </span>
                                          <span className="ml-2">
                                            {(
                                              (1 - getScontoFinalValue(id) / 100) *
                                              toFloat(cart[id], 0) *
                                              toFloat(element.cost, 0)
                                            ).toFixed(2)}
                                          </span>
                                        </>
                                      ) : (
                                        (toFloat(cart[id], 0) * toFloat(element.cost, 0)).toFixed(2)
                                      )}
                                      €
                                    </>
                                  ) : null}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        {element.imageUrl !== "" && element.imageUrl !== "0" ? (
                          <div className="symbol symbol-70 flex-shrink-0">
                            <img src={toAbsoluteUrl(element.imageUrl)} alt="" />
                          </div>
                        ) : null}
                      </div>
                      <div className="separator separator-solid"></div>
                    </React.Fragment>
                  );
                })}
              <div className="p-8">
                {hasColli ? (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <span className="font-weight-bold text-muted font-size-sm mr-2">Numero di colli</span>
                      <span className="font-weight-bolder text-primary text-right">
                        {Object.values(cart).reduce((sum, v) => sum + toFloat(v, 0), 0)}
                      </span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-7">
                      <span className="font-weight-bold text-muted font-size-sm mr-2">
                        {displayName.charAt(0).toUpperCase() + displayName.slice(1)} totali
                      </span>
                      <span className="font-weight-bolder text-dark-50 text-right">
                        {Object.keys(cart).reduce((sum, id) => {
                          const key = normalizeProductKey(id);
                          const element = elementsMap.get(key) || elementsMap.get(id);
                          if (!element) return sum;
                          if (!element.quantity) return sum;
                          return sum + parseInt(element.quantity, 10) * toFloat(cart[id], 0);
                        }, 0)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <span className="font-weight-bold text-muted font-size-sm mr-2">Numero totale</span>
                    <span className="font-weight-bolder text-primary text-right">
                      {Object.values(cart).reduce((sum, v) => sum + toFloat(v, 0), 0)}
                    </span>
                  </div>
                )}
                <div className="d-flex align-items-center justify-content-between mb-7">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Valore totale</span>
                  <span className="font-weight-bolder text-dark-50 text-right">
                    {Object.keys(cart)
                      .reduce((sum, id) => {
                        const key = normalizeProductKey(id);
                        const element = elementsMap.get(key) || elementsMap.get(id);
                        if (!element) return sum;
                        if (typeof element.cost === "undefined") return sum;
                        return sum + toFloat(element.cost, 0) * toFloat(cart[id], 0);
                      }, 0)
                      .toFixed(2)}
                    €
                  </span>
                </div>
                {richiediDatiVendita && Object.keys(cart).some((id) => getScontoFinalValue(id) > 0) ? (
                  <div className="d-flex align-items-center justify-content-between mb-7">
                    <span className="font-weight-bold text-muted font-size-sm mr-2">Valore totale scontato</span>
                    <span className="font-weight-bolder text-dark-50 text-right">
                      {Object.keys(cart)
                        .reduce((sum, id) => {
                          const key = normalizeProductKey(id);
                          const element = elementsMap.get(key) || elementsMap.get(id);
                          if (!element) return sum;
                          if (typeof element.cost === "undefined") return sum;
                          return (
                            sum +
                            (1 - getScontoFinalValue(id) / 100) * toFloat(element.cost, 0) * toFloat(cart[id], 0)
                          );
                        }, 0)
                        .toFixed(2)}
                      €
                    </span>
                  </div>
                ) : null}
              </div>
            </PerfectScrollbar>
          </Dropdown.Menu>
        ) : null}

        <Dropdown.Toggle as={CartDropdownToggler} color={color} id="dropdown-toggle-my-cart-toggle">
          <span className="label label-white label-pill label-inline label-sm mr-1">{Object.keys(cart).length}</span>{" "}
          {displayName}
          <span className="svg-icon svg-icon-md m-3">
            <SVG src={toAbsoluteUrl(icons.addToCart)} />
          </span>
          {Object.keys(cart).length > 0 ? <i className="ki ki-arrow-up icon-xs"></i> : <i className="ki ki-arrow-up icon-xs invisible"></i>}
        </Dropdown.Toggle>
      </Dropdown>
    </div>
  );
});

ShopCartDropdown.propTypes = {
  cart: PropTypes.object.isRequired,
  displayName: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  elementsMap: PropTypes.instanceOf(Map).isRequired,
  hasColli: PropTypes.bool.isRequired,
  maxDescriptionLength: PropTypes.number.isRequired,
  richiediDatiVendita: PropTypes.bool.isRequired,
  discountValidationMessage: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  discountsLoadError: PropTypes.string.isRequired,
  onSendRequest: PropTypes.func.isRequired,
  sendDisabled: PropTypes.bool.isRequired,
  sendLabel: PropTypes.string.isRequired,
  getScontoFinalValue: PropTypes.func.isRequired,
  getEffectiveDiscountRaw: PropTypes.func.isRequired,
  setCartValue: PropTypes.func.isRequired,
};
